import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    sale_date: string;
    sale_price: string;
    buyer_name: string;
    buyer_contact: string;
    odometer_reading?: string;
    notes?: string;
    payment_document: FileList;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function AddSoldVehicle() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const data: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                setVehicles([]);
                return;
            }
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                const activeVehicles = Array.isArray(response) ? response.filter((v: any) => v.status === 'Active') : [];
                setVehicles(activeVehicles);
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };
        fetchVehicles();
        setValue("vehicle_id", "");
    }, [selectedBranch, setValue]);

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            const formData = new FormData();

            // Append all text fields
            Object.keys(data).forEach(key => {
                if (key !== 'payment_document') {
                    const value = (data as any)[key];
                    if (value instanceof Date) {
                        formData.append(key, formatDate(value));
                    } else {
                        formData.append(key, value || '');
                    }
                }
            });

            // Append File
            if (data.payment_document && data.payment_document.length > 0) {
                formData.append('payment_document', data.payment_document[0]);
            }

            await apiPost("vehicle_sales/store", formData);
            toast.success("Vehicle Sale Recorded Successfully!", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/sold_vehicle`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error recording sale");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Record Sale" parent="Vehicle Sales" title="Add Sold Vehicle" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <H5>Sale Details</H5>
                            <hr />
                            <Row className="g-3">
                                <Col md={6}>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="branch_id"
                                        control={control}
                                        rules={{ required: "Branch is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                    <option value="">Select Branch</option>
                                                    {branches.map((b: any) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.branch_id?.message as string}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Select Vehicle <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vehicle_id"
                                        control={control}
                                        rules={{ required: "Vehicle is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input
                                                    type="select"
                                                    {...field}
                                                    invalid={!!errors.vehicle_id}
                                                    disabled={!selectedBranch}
                                                >
                                                    <option value="">Select Vehicle</option>
                                                    {vehicles.map((v: any) => (
                                                        <option key={v.id} value={v.id}>
                                                            {v.registration_number} - {v.brand} {v.model}
                                                        </option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.vehicle_id?.message as string}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={4}>
                                    <Label>Sale Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="sale_date"
                                        control={control}
                                        rules={{ required: "Sale Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.sale_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        field.onChange(d);
                                                    }}
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText="DD/MM/YYYY"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.sale_date?.message as string}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Sale Price <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="sale_price"
                                        control={control}
                                        rules={{ required: "Sale Price is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    {...field}
                                                    placeholder="0.00"
                                                    invalid={!!errors.sale_price}
                                                    autoComplete="off"
                                                />
                                                <FormFeedback>{errors.sale_price?.message as string}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Odometer Reading</Label>
                                    <Controller
                                        name="odometer_reading"
                                        control={control}
                                        render={({ field }) => (
                                            <Input {...field} value={field.value || ''} placeholder="e.g. 150000 km" autoComplete="off" />
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Buyer Name <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="buyer_name"
                                        control={control}
                                        rules={{ required: "Buyer Name is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Name of Buyer/Company" invalid={!!errors.buyer_name} autoComplete="off" />
                                                <FormFeedback>{errors.buyer_name?.message as string}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Buyer Contact <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="buyer_contact"
                                        control={control}
                                        rules={{ required: "Buyer Contact is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Phone / Email" invalid={!!errors.buyer_contact} autoComplete="off" />
                                                <FormFeedback>{errors.buyer_contact?.message as string}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={12}>
                                    <Label>Notes</Label>
                                    <Controller
                                        name="notes"
                                        control={control}
                                        render={({ field }) => (
                                            <Input type="textarea" {...field} value={field.value || ''} placeholder="Additional details..." autoComplete="off" />
                                        )}
                                    />
                                </Col>
                                <Col md={12}>
                                    <Label>Payment Document (Image/PDF) <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="payment_document"
                                        control={control}
                                        rules={{ required: "Payment Document is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const file = e.target.files[0];
                                                            if (file.size > 2 * 1024 * 1024) {
                                                                toast.error("File size must be less than 2MB");
                                                                e.target.value = "";
                                                                onChange(null);
                                                                return;
                                                            }
                                                        }
                                                        onChange(e.target.files);
                                                    }}
                                                    accept="image/*,.pdf"
                                                    invalid={!!errors.payment_document}
                                                />
                                                <FormFeedback>{errors.payment_document?.message as string}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <div className="mt-4">
                                <Btn color="primary" disabled={loading} type="submit">
                                    {loading ? <>Submitting <Spinner size="sm" /></> : "Submit"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
